import Axios from 'api/client';

interface UploadFileResponse {
    [id: string]: {
        id: string;
        name: string;
        url: string;
    };
}

interface RemoteUploadResponse {
    url: string;
}

/**
 * https://stackoverflow.com/questions/44865121/canvas-tainted-by-cors-data-and-s3/44866772#44866772
 *
 * TLDR: Images cached in the browser via <img=> tag are returned via XHR request, but do not contain an 'allowed-origin' header.
 *      Add a cache-busting URL param to ensure XHR requests are cached separately and contain the CORS headers in chrome.
 *
 * @param url
 */
const getFile = async (url: string) => {
    const { headers, data } = await Axios.get(url, {
        headers: {
            'Access-Control-Allow-Origin': '*',
            Authorization: '',
        },
        params: {
            cacheBust: 'dash',
        },
        responseType: 'blob',
        withCredentials: false,
        baseURL: undefined,
    });

    const mimeType = headers['content-type'];
    return new File([data], url, { type: mimeType });
};

const uploadFile = async (blob: Blob, filename?: string) => {
    const formData = new FormData();

    formData.append('files[]', blob, filename);

    const { data, ...rest } = await Axios.post<UploadFileResponse>(`/upload`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });

    const transformedResponse = data[Object.keys(data)[0]];

    return {
        data: transformedResponse,
        ...rest,
    };
};

const uploadRemoteMedia = (accountId: number, url: string) =>
    Axios.post<RemoteUploadResponse>(`accounts/${accountId}/remote-media`, {
        url,
        type: 'posts',
    });

const getMediaPreview = (url: string) =>
    Axios.get<{
        title: string;
        images: string[];
    }>('/api/fetchLinkPreview', {
        baseURL: '',
        params: {
            url,
        },
    });

type CollectionType = 'meta' | 'cover' | 'profile';
type AccountCollectionType = CollectionType | 'logos';
type AccountMediaType = 'default' | 'email';

const uploadCampaignMedia = (campaignId: number, file: File, collection?: CollectionType) => {
    const formData = new FormData();

    formData.append('files[]', file);

    return Axios.post<RemoteUploadResponse>(`/campaigns/${campaignId}/media`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        params: {
            collection,
        },
    });
};

const uploadAccountMedia = (
    accountId: number,
    file: File,
    collection?: AccountCollectionType,
    type?: AccountMediaType,
) => {
    const formData = new FormData();

    formData.append('files[]', file);

    return Axios.post<RemoteUploadResponse>(`/accounts/${accountId}/media`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        params: {
            collection,
            type,
        },
    });
};

const uploadCampaignMemberMedia = (
    campaignId: number,
    memberId: number,
    file: File | Blob,
    collection?: CollectionType,
) => {
    const formData = new FormData();

    formData.append('files[]', file);

    return Axios.post<RemoteUploadResponse>(
        `/campaigns/${campaignId}/members/${memberId}/media`,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            params: {
                collection,
            },
        },
    );
};

const deleteCampaignMediaByUrl = (campaignId: number, url: string) =>
    Axios.delete(`/campaigns/${campaignId}/media/${url.split('/').pop()}`);

const deleteAccountMediaByUrl = (accountId: number, url: string) =>
    Axios.delete(`/accounts/${accountId}/media/${url.split('/').pop()}`);

const uploadCampaignMatchMedia = (
    campaignId: number,
    matchId: number,
    file: File,
    collection = 'cover',
) => {
    const formData = new FormData();

    formData.append('files[]', file);

    return Axios.post<RemoteUploadResponse>(
        `/campaigns/${campaignId}/matches/${matchId}/media`,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            params: {
                collection,
            },
        },
    );
};

const deleteCampaignMatchMediaByUrl = (campaignId: number, matchId: number, url?: string) => {
    if (!url) {
        return new Promise(() => {});
    }
    return Axios.delete(
        `/campaigns/${campaignId}/matches/${matchId}/media/${url.split('/').pop()}`,
    );
};

export {
    getFile,
    uploadFile,
    uploadRemoteMedia,
    getMediaPreview,
    uploadCampaignMedia,
    deleteCampaignMediaByUrl,
    uploadCampaignMatchMedia,
    deleteCampaignMatchMediaByUrl,
    uploadCampaignMemberMedia,
    uploadAccountMedia,
    deleteAccountMediaByUrl,
};
export type { CollectionType, AccountMediaType };
